import { ReactElement, useContext } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";
import { MedicalItemCard } from "@/DesignSystem/Molecules/MedicalItemCard/MedicalItemCard";
import { MedicalItemCardList } from "@/DesignSystem/Molecules/MedicalItemCard/MedicalItemCardList";
import { SearchInput } from "@/DesignSystem/Molecules/SearchField/SearchInput";
import { EmployeeContext } from "@/Flowsheets/Contexts/EmployeeContext";
import { FlowsheetHistoryHelper } from "@/Flowsheets/Helpers/FlowsheetHistoryHelper";
import { useGetFlowsheetName } from "@/Flowsheets/Hooks/useGetFlowsheetName";
import { FlowsheetSummary } from "@/Flowsheets/Models/FlowsheetSummary";

import styles from "./FlowsheetHistory.module.scss";

import ArrowBackIcon from "@/Globals/_images/icons/arrow-back.svg";

interface IFlowsheetHistoryProps {
    /**
     * The ContactFacetId of the patient the flowsheet belongs to
     */
    readonly patientId: Guid;

    /**
     * The Id of the selected flowsheet
     */
    readonly selectedFlowsheetId: Guid;

    /**
     * The event when selecting a flowsheet in the history
     * @returns void
     */
    onFlowsheetSelect: (id: FlowsheetSummary) => void;

    /**
     * The event when leaving the flowsheet
     * @returns void
     */
    leaveFlowsheet: () => void;

    /**
     * The event when setting the search term
     * @returns void
     */
    onSearch: (term: string) => void;

    /**
     * The search term to filter the flowsheet history
     * @returns string
     * */
    searchTerm: string;

    /**
     * The filtered flowsheet history
     * @returns FlowsheetSummary[]
     */
    filteredHistory: FlowsheetSummary[];
}

function FlowsheetHistory(props: Readonly<IFlowsheetHistoryProps>): ReactElement {
    const { getText } = useTranslation();
    const { getFullNameWithPrefixContactFacet } = useContext(EmployeeContext);
    const { getName } = useGetFlowsheetName();

    return (
        <div className={styles.flowsheetHistoryContainer}>
            <div className={styles.backToConsultationButton}>
                <button
                    className="btn btn-primary"
                    onClick={() => props.leaveFlowsheet()}
                >
                    <ArrowBackIcon className="push-right-5" />
                    <span>{getText("BackToConsultationLabel")}</span>
                </button>
            </div>
            <div className={styles.searchContainer}>
                <SearchInput
                    width="100%"
                    onChange={e => props.onSearch(e.target.value)}
                />
            </div>
            <div className={styles.listContainer}>
                {props.filteredHistory && (
                    <MedicalItemCardList>
                        {props.filteredHistory.map(flowsheet => {
                            const isSelected = props.selectedFlowsheetId === flowsheet.Id;

                            return (
                                <MedicalItemCard
                                    key={`flowsheet-${flowsheet.Id}`}
                                    id={flowsheet.Id}
                                    title={getName(flowsheet.Template)}
                                    date={flowsheet.CreationDate.format("YYYY-MM-DD")}
                                    names={Object.values(FlowsheetHistoryHelper.latestEventByEmployee(flowsheet)).map(history => getFullNameWithPrefixContactFacet(history.EmployeeId))}
                                    fullWidth
                                    selected={isSelected}
                                    onClick={() => {
                                        if (!isSelected)
                                            props.onFlowsheetSelect(flowsheet);
                                    }}
                                    status={flowsheet.ApprovalDate ? "none" : "draft"}
                                />
                            );
                        })}
                    </MedicalItemCardList>
                )}
            </div>
        </div>
    );
}

export { FlowsheetHistory };
